import React, { useEffect, useMemo, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';

// Contexts
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

// Utils
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { LISTING_STATE_PENDING_APPROVAL, LISTING_STATE_CLOSED } from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import { FINAL_SALE_RETURN_POLICY, listingMetaDescription } from '../../util/seo';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
} from '../../util/urlHelpers';
import { createResourceLocatorString } from '../../util/routes';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors.js';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers.js';
import { requireListingImage } from '../../util/configHelpers';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';
import { getPayoutFlowState } from '../../util/payoutFlow';
import {
  OFFER,
  REQUEST,
  isBookingProcess,
  isNegotiationProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';
import { withViewport } from '../../util/uiHelpers';
import { computeLikesCount, isListingFavorited } from '../../util/favoritesHelpers';
import * as listingVisibility from '../../util/listingVisibility';

// Global ducks (for Redux actions and thunks)
import { getListingsById, getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';
import { selectFavoriteIds, toggleFavorite } from '../../ducks/favorites.duck';
import { addItemToCart, removeItemFromCart } from '../../ducks/cart.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { resolveMaxQuantityForListing } from '../../util/cartHelpers';
import { isPhoneVerifiedForUser } from '../../util/phoneNumber';

// Shared components
import {
  ExternalLink,
  IconSpinner,
  LayoutSingleColumn,
  ListSlider,
  NamedLink,
  NamedRedirect,
  Page,
  PrimaryButton,
  SecondaryButton,
  ShareModal,
} from '../../components';

// Related components and modules
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import {
  createSimilarListingsFilterKey,
  fetchSimilarListings,
  getSimilarListingFilterValues,
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
} from './ListingPage.duck';
import { useLazySimilarListings } from './useLazySimilarListings';

import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  handleContactUser,
  handleSubmitInquiry,
  handleNavigateToMakeOfferPage,
  handleNavigateToRequestQuotePage,
  handleSubmit,
  priceForSchemaMaybe,
  productBrandSchemaMaybe,
} from './ListingPage.shared';
import ActionBarMaybe from './ActionBarMaybe';
import SectionAuthorMaybe from './SectionAuthorMaybe';
import SectionGallery from './SectionGallery';
import SectionTags from './SectionTags';

import css from './ListingPage.module.css';
import Icons from '../../components/Icons/Icons.js';
import IconHeartFilled from '../../components/IconHeartFilled/IconHeartFilled.js';
import IconHeart from '../../components/IconHeart/IconHeart.js';
import IconBag from '../../components/IconBag/IconBag.js';
import IconBagFilled from '../../components/IconBagFilled/IconBagFilled.js';
import IconMessage from '../../components/IconMessage/IconMessage.js';
import { getStoreName, getUsername } from '../../util/dataExtractors.js';
import { formatMoney } from '../../util/currency.js';
import { getListingSale } from '../../util/listingSale';
import { getListingShippingInfo } from '../../util/listingShipping.js';
import {
  getFirstAvailableSizeOption,
  getListingSizeChoices,
  getListingSizeDisplay,
  normalizeSizeValue,
  resolveSelectedSizeOption,
  toSizeOption,
} from '../../util/sizeHelpers.js';
import { useOptimisticCartState } from './useOptimisticCartState';
import { useTrackViewedProduct } from '../../util/klaviyo';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;
const DEBUG_PRIVATE_LISTINGS =
  typeof process !== 'undefined' && process.env.DEBUG_PRIVATE_LISTINGS === 'true';

const { UUID } = sdkTypes;
const {
  canDirectlyTransactOnListing,
  canDirectlyViewListing,
  filterPublicListings,
  isVacationModeListing,
  isPrivateListing,
} = listingVisibility;

const valueToLabel = value => {
  if (value == null) {
    return null;
  }

  if (typeof value === 'object') {
    return value?.label || value?.name || value?.value || null;
  }

  if (typeof value === 'string') {
    return value
      .split('-')
      .join(' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  }

  return `${value}`;
};

export const ListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );
  const [inquiryType, setInquiryType] = useState('inquiry');
  const [phoneVerificationRequired, setPhoneVerificationRequired] = useState(
    Boolean(
      props.inquiryModalOpenForListingId === props.params.id &&
        props.currentUser &&
        !isPhoneVerifiedForUser(props.currentUser)
    )
  );
  const [phoneVerificationSuccess, setPhoneVerificationSuccess] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [cachedSaveCountBySeller, setCachedSaveCountBySeller] = useState(
    props.saveCountBySeller || 0
  );
  const [selectedSize, setSelectedSize] = useState('');
  const [sizeSelectionError, setSizeSelectionError] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    reviews = [],
    fetchReviewsError,
    sendInquiryInProgress,
    sendInquiryError,
    history,
    callSetInitialValues,
    onSendInquiry,
    onRefreshCurrentUser,
    onInitializeCardPaymentData,
    config,
    routeConfiguration,
    showOwnListingsOnly,
    favoriteIds,
    favoriteInProgressById,
    onToggleFavorite,
    onAddToCart,
    onRemoveFromCart,
    cartItems,
    saveCountBySeller,
    viewport,
    authorListings,
    variantListings = [],
    similarListings = [],
    fetchSimilarListingsInProgress,
    onFetchSimilarListings,
  } = props;

  const listingConfig = config.listing;
  const listingId = new UUID(rawParams.id);
  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
  const currentListing =
    isPendingApprovalVariant || isDraftVariant || showOwnListingsOnly
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));
  const routeListingUuid = currentListing?.id?.uuid;

  useEffect(() => {
    setCachedSaveCountBySeller(saveCountBySeller || 0);
  }, [saveCountBySeller, routeListingUuid]);

  useTrackViewedProduct(currentListing);

  const baseListingPublicData = currentListing?.attributes?.publicData || {};
  const publicDataForSimilar = baseListingPublicData;
  const variantCandidates = useMemo(() => {
    const seenListingIds = new Set();
    const listings = [];

    const maybePushListing = listing => {
      const id = listing?.id?.uuid;
      if (!id || seenListingIds.has(id)) {
        return;
      }

      seenListingIds.add(id);
      listings.push(listing);
    };

    maybePushListing(currentListing);
    variantListings.forEach(maybePushListing);

    return listings;
  }, [currentListing, variantListings]);
  const sizeChoicesWithListing = useMemo(() => {
    const choiceBySize = new Map();

    variantCandidates.forEach(listing => {
      const listingId = listing?.id?.uuid;
      if (!listingId) {
        return;
      }

      const publicData = listing?.attributes?.publicData || {};
      const sizeOption = toSizeOption(
        publicData?.sizeOption || publicData?.size || publicData?.sizeLabel
      );
      const normalizedSizeValue = normalizeSizeValue(sizeOption?.values);
      if (!normalizedSizeValue) {
        return;
      }

      const rawStock = Number.parseInt(listing?.currentStock?.attributes?.quantity, 10);
      const stock = Number.isFinite(rawStock) ? rawStock : null;
      const nextChoice = {
        values: normalizedSizeValue,
        label: sizeOption?.label || normalizedSizeValue,
        listingId,
        stock,
        isChildVariant: !!publicData?.parentId,
      };

      const existingChoice = choiceBySize.get(normalizedSizeValue);
      if (!existingChoice) {
        choiceBySize.set(normalizedSizeValue, nextChoice);
        return;
      }

      const nextInStock = nextChoice.stock == null || nextChoice.stock > 0;
      const existingInStock = existingChoice.stock == null || existingChoice.stock > 0;
      if (nextInStock && !existingInStock) {
        choiceBySize.set(normalizedSizeValue, nextChoice);
      } else if (nextChoice.isChildVariant && !existingChoice.isChildVariant) {
        choiceBySize.set(normalizedSizeValue, nextChoice);
      }
    });

    if (choiceBySize.size > 0) {
      return Array.from(choiceBySize.values());
    }

    return getListingSizeChoices(baseListingPublicData)
      .map(option => ({
        ...option,
        listingId: routeListingUuid || null,
        stock: null,
      }))
      .filter(option => !!option?.values);
  }, [variantCandidates, baseListingPublicData, routeListingUuid]);
  const defaultSizeOption = useMemo(() => {
    if (!sizeChoicesWithListing.length) {
      return getFirstAvailableSizeOption(baseListingPublicData);
    }
    const inStockChoice =
      sizeChoicesWithListing.find(choice => choice?.stock == null || choice.stock > 0) || null;
    return inStockChoice || sizeChoicesWithListing[0];
  }, [sizeChoicesWithListing, baseListingPublicData]);
  const selectedSizeOption = useMemo(() => {
    if (!sizeChoicesWithListing.length) {
      return selectedSize
        ? resolveSelectedSizeOption(baseListingPublicData, selectedSize)
        : defaultSizeOption;
    }

    const normalizedSelectedSize = normalizeSizeValue(selectedSize);
    if (!normalizedSelectedSize) {
      return defaultSizeOption || sizeChoicesWithListing[0];
    }

    return (
      sizeChoicesWithListing.find(
        option =>
          normalizeSizeValue(option?.values) === normalizedSelectedSize ||
          normalizeSizeValue(option?.label) === normalizedSelectedSize
      ) ||
      defaultSizeOption ||
      sizeChoicesWithListing[0]
    );
  }, [sizeChoicesWithListing, selectedSize, baseListingPublicData, defaultSizeOption]);
  const selectedSizeValue = selectedSizeOption?.values || '';
  const hasMultipleSizes = sizeChoicesWithListing.length > 1;
  const activeListingId = selectedSizeOption?.listingId || routeListingUuid || null;
  const activeListing = useMemo(() => {
    if (!activeListingId) {
      return currentListing;
    }
    return (
      variantCandidates.find(listing => listing?.id?.uuid === activeListingId) || currentListing
    );
  }, [activeListingId, currentListing, variantCandidates]);
  const listingPublicData = activeListing?.attributes?.publicData || baseListingPublicData;
  const isVacationMode = isVacationModeListing(activeListing);
  const { isListingInCart, isCartActionPending, runOptimisticCartAction } = useOptimisticCartState(
    cartItems
  );
  const vacationModeListingIds = useMemo(
    () =>
      Array.from(
        new Set(
          [currentListing, activeListing]
            .filter(listing => isVacationModeListing(listing))
            .map(listing => listing?.id?.uuid)
            .filter(Boolean)
        )
      ),
    [activeListing, currentListing]
  );
  const vacationModeCartListingIds = useMemo(
    () => vacationModeListingIds.filter(listingId => Boolean(cartItems?.[listingId])),
    [cartItems, vacationModeListingIds]
  );

  useEffect(() => {
    setSelectedSize('');
    setSizeSelectionError(false);
    setPhoneVerificationRequired(false);
    setPhoneVerificationSuccess(false);
  }, [routeListingUuid]);

  useEffect(() => {
    if (inquiryModalOpen && currentUser && inquiryType === 'inquiry') {
      setPhoneVerificationRequired(!isPhoneVerifiedForUser(currentUser));
    }
  }, [currentUser, inquiryModalOpen, inquiryType]);

  useEffect(() => {
    if (!vacationModeCartListingIds.length) {
      return;
    }

    vacationModeCartListingIds.forEach(listingId => {
      onRemoveFromCart(listingId);
    });
  }, [onRemoveFromCart, vacationModeCartListingIds]);

  const { brandValues, sizeValues } = useMemo(
    () => getSimilarListingFilterValues(publicDataForSimilar),
    [publicDataForSimilar]
  );
  const similarFilterKey = useMemo(
    () => createSimilarListingsFilterKey({ brandValues, sizeValues }),
    [brandValues, sizeValues]
  );
  const { hasSimilarFilters, hasIntersected, similarListingsObserverRef } = useLazySimilarListings({
    listingUuid: routeListingUuid,
    brandValues,
    sizeValues,
    similarFilterKey,
    config,
    onFetchSimilarListings,
  });

  const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'details';

  const isApproved =
    activeListing.id && activeListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  const topbar = <TopbarContainer />;

  if (showListingError && showListingError.status === 404) {
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (showListingError) {
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (!currentListing.id) {
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  const viewerUserId = currentUser?.id?.uuid || null;
  const canViewActiveListing = canDirectlyViewListing(activeListing, viewerUserId);
  const canTransactOnActiveListing = canDirectlyTransactOnListing(activeListing, viewerUserId);
  const activeListingPrivate = isPrivateListing(activeListing);

  if (DEBUG_PRIVATE_LISTINGS) {
    // eslint-disable-next-line no-console
    console.log('[private-listings] listing-page-guard', {
      viewerId: viewerUserId,
      listingId: activeListing?.id?.uuid || activeListing?.id || null,
      isPrivate: activeListingPrivate,
      canView: canViewActiveListing,
      canTransact: canTransactOnActiveListing,
    });
  }

  if (!canViewActiveListing) {
    return <NotFoundPage staticContext={props.staticContext} />;
  }

  const {
    description = '',
    geolocation = null,
    price = null,
    title = '',
    metadata = {},
  } = activeListing.attributes;
  const publicData = listingPublicData;

  const richTitle = (
    <span>
      {richText(title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const authorAvailable = activeListing && activeListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && activeListing.author.id.uuid === currentUser.id.uuid;

  const { listingType, transactionProcessAlias, unitType } = publicData;
  if (!(listingType && transactionProcessAlias && unitType)) {
    return (
      <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} invalidListing />
    );
  }
  const validListingTypes = listingConfig.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const isNegotiation = isNegotiationProcess(processName);
  const processType = isBooking
    ? 'booking'
    : isPurchase
    ? 'purchase'
    : isNegotiation
    ? 'negotiation'
    : 'inquiry';

  const currentAuthor = authorAvailable ? activeListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);
  const authorNeedsPayoutDetails =
    ['booking', 'purchase'].includes(processType) || (isNegotiation && unitType === OFFER);
  const currentUserPayoutFlow = getPayoutFlowState(currentUser);
  const noPayoutDetailsSetWithOwnListing =
    isOwnListing && authorNeedsPayoutDetails && currentUserPayoutFlow.shouldRequirePayoutOnboarding;

  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');
  const sellerId = currentAuthor?.id?.uuid;
  const sellerUsername = getUsername(currentAuthor);
  const sellerStoreName = getStoreName(currentAuthor);
  const soldByName = sellerStoreName || authorDisplayName;
  const soldByLinkProps = sellerUsername
    ? {
        name: 'StorePage',
        params: { username: sellerUsername },
      }
    : sellerId
    ? {
        name: 'ProfilePage',
        params: { id: sellerId },
        to: { state: { authorId: sellerId } },
      }
    : null;
  const { formattedPrice } = priceData(price, config.currency, intl);
  const sale = getListingSale(currentListing);
  const shippingInfo = getListingShippingInfo({ listing: currentListing, price });
  const pdpShippingLabel =
    shippingInfo?.shippingLabelType === 'free'
      ? intl.formatMessage({ id: 'ListingPage.freeShippingInline' })
      : shippingInfo?.shippingLabelType === 'amount' && shippingInfo.shippingAmount
      ? intl.formatMessage(
          { id: 'ListingPage.shippingPriceInline' },
          { shippingPrice: formatMoney(intl, shippingInfo.shippingAmount) }
        )
      : null;

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
    setInquiryType,
    setPhoneVerificationRequired,
    setPhoneVerificationSuccess,
  });
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
  });
  const onNavigateToMakeOfferPage = handleNavigateToMakeOfferPage({
    ...commonParams,
    getListing,
  });
  const onNavigateToRequestQuotePage = handleNavigateToRequestQuotePage({
    ...commonParams,
    getListing,
  });
  const onSubmit = handleSubmit({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    getListing,
    onInitializeCardPaymentData,
  });

  const handleOrderSubmit = values => {
    const isCurrentlyClosed = activeListing.attributes.state === LISTING_STATE_CLOSED;
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else if (isNegotiation && unitType === REQUEST) {
      onNavigateToMakeOfferPage(values);
    } else if (isNegotiation && unitType === OFFER) {
      onNavigateToRequestQuotePage(values);
    } else {
      onSubmit(values);
    }
  };

  const facebookImages = listingImages(activeListing, 'facebook');
  const twitterImages = listingImages(activeListing, 'twitter');
  const schemaImages = listingImages(
    activeListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);
  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title, price: formattedPrice, marketplaceName }
  );
  // Same address as the page's canonical URL (/l/{id}), not the one the visitor happened to use
  const productURL = `${config.marketplaceRootURL}/l/${routeListingUuid}`;
  const metaSizeDisplay = getListingSizeDisplay(publicData);
  const metaDescription = listingMetaDescription({
    brand: valueToLabel(publicData?.brandOption || publicData?.brand),
    type: valueToLabel(publicData?.type),
    size: metaSizeDisplay?.isMultiple
      ? null
      : metaSizeDisplay?.label ||
        valueToLabel(publicData?.sizeOption || publicData?.size || publicData?.sizeLabel),
    condition: valueToLabel(publicData?.conditionOption || publicData?.condition),
    price: formattedPrice,
    description,
    marketplaceName,
  });
  const currentStock = activeListing.currentStock?.attributes?.quantity || 0;
  const isListingClosed = activeListing.attributes.state === LISTING_STATE_CLOSED;
  const schemaAvailability =
    isListingClosed || (activeListing.currentStock && currentStock <= 0)
      ? 'https://schema.org/OutOfStock'
      : activeListing.currentStock
      ? 'https://schema.org/InStock'
      : null;

  const availabilityMaybe = schemaAvailability ? { availability: schemaAvailability } : {};
  const schemaCondition = valueToLabel(publicData?.conditionOption || publicData?.condition);
  const itemConditionMaybe = schemaCondition
    ? {
        itemCondition:
          schemaCondition.toLowerCase() === 'new'
            ? 'https://schema.org/NewCondition'
            : 'https://schema.org/UsedCondition',
      }
    : {};
  const schemaBrand = valueToLabel(publicData?.brandOption || publicData?.brand);
  const brandSchemaMaybe = schemaBrand
    ? { brand: { '@type': 'Brand', name: schemaBrand } }
    : productBrandSchemaMaybe(publicData);
  const schemaColor = valueToLabel(publicData?.colorOption || publicData?.color);
  const sellerSchemaMaybe = authorDisplayName
    ? { seller: { '@type': 'Person', name: authorDisplayName } }
    : {};
  // Private listings are only for people the seller sends the link to. Vacation listings stay
  // indexed: the seller is coming back, and re-earning rankings afterwards takes weeks.
  const noIndexMaybe =
    activeListing.attributes.state === LISTING_STATE_CLOSED || activeListingPrivate
      ? { noIndex: true }
      : {};

  const isFavoriteUpdating = !!favoriteInProgressById?.[routeListingUuid];
  const isFavorited = isListingFavorited(favoriteIds, routeListingUuid);
  const isInCart = isListingInCart(activeListingId);
  const isActiveListingCartActionPending = isCartActionPending(activeListingId);
  const favoriteActionDisabled = isFavoriteUpdating || isOwnListing || !canTransactOnActiveListing;
  const likesCount = computeLikesCount({
    listing: activeListing,
    favoriteIds,
    currentUserId: currentUser?.id?.uuid,
  });

  const breadcrumbData = [
    { queryKey: 'category', rawValue: publicData?.category },
    { queryKey: 'type', rawValue: publicData?.type },
    { queryKey: 'brand', rawValue: publicData?.brand },
  ]
    .map(item => {
      const label = valueToLabel(item.rawValue);
      const queryValue =
        typeof item.rawValue === 'string'
          ? item.rawValue
          : item.rawValue?.value ||
            item.rawValue?.key ||
            (typeof label === 'string' ? label.toLowerCase().replace(/\s+/g, '-') : '');

      return {
        ...item,
        label,
        queryValue,
      };
    })
    .filter(item => !!item.label)
    .slice(0, 3)
    .filter(Boolean);

  const listingSizeDisplay = getListingSizeDisplay(publicData);
  const listingSizeValue = selectedSizeOption?.label
    ? selectedSizeOption.label
    : listingSizeDisplay?.isMultiple
    ? intl.formatMessage({
        id: 'ListingPage.multipleSizesAvailable',
        defaultMessage: 'Multiple sizes available',
      })
    : listingSizeDisplay?.label ||
      valueToLabel(publicData?.sizeOption || publicData?.size || publicData?.sizeLabel);
  const listingDetails = [
    {
      id: 'ListingPage.detail.brand',
      defaultLabel: 'Brand',
      value: valueToLabel(publicData?.brandOption || publicData?.brand),
    },
    {
      id: 'ListingPage.detail.color',
      defaultLabel: 'Color',
      value: valueToLabel(publicData?.colorOption || publicData?.color),
    },
    {
      id: 'ListingPage.detail.category',
      defaultLabel: 'Category',
      value: valueToLabel(publicData?.category),
    },
    {
      id: 'ListingPage.detail.condition',
      defaultLabel: 'Condition',
      value: valueToLabel(publicData?.conditionOption || publicData?.condition),
    },
    {
      id: 'ListingPage.detail.size',
      defaultLabel: 'Size',
      value: listingSizeValue,
    },
    {
      id: 'ListingPage.detail.type',
      defaultLabel: 'Type',
      value: valueToLabel(publicData?.typeOption || publicData?.type),
    },
  ].filter(item => !!item.value);

  const isCurrentlyClosed = activeListing.attributes.state === LISTING_STATE_CLOSED;
  const isOutOfStock = activeListing.currentStock ? currentStock <= 0 : false;
  const disableActions =
    isOwnListing ||
    isCurrentlyClosed ||
    isOutOfStock ||
    isVacationMode ||
    !canTransactOnActiveListing;
  const selectedSizeMaybe = selectedSizeOption
    ? {
        selectedSize: selectedSizeOption.values,
        selectedSizeLabel: selectedSizeOption.label,
      }
    : {};
  const selectedSizeMaxQuantity = resolveMaxQuantityForListing(
    activeListing,
    selectedSizeOption?.values || null
  );
  const cartUnavailable = !activeListingId || isOutOfStock;
  const showCartActions = !isOwnListing && (!disableActions || isInCart);
  const disablePrimaryCartButton = cartUnavailable || isActiveListingCartActionPending;
  const primaryCartMessageId = isInCart ? 'ListingPage.removeFromCart' : 'ListingPage.addToCart';

  const addActiveListingToCart = ({ quantity = 1 } = {}) => {
    if (cartUnavailable) {
      return;
    }

    if (!ensureSizeIfNeeded()) {
      return;
    }

    onAddToCart({
      listingId: activeListingId,
      quantity,
      sellerId: currentAuthor?.id?.uuid,
      maxQuantity: selectedSizeMaxQuantity,
      ...selectedSizeMaybe,
    });

    if (isAuthenticated && !isInCart) {
      setCachedSaveCountBySeller(prevCount => prevCount + 1);
    }
  };

  const ensureSizeIfNeeded = () => {
    if (!hasMultipleSizes) {
      return true;
    }

    const selectedSizeInStock = selectedSizeOption?.stock == null || selectedSizeOption?.stock > 0;
    if (selectedSizeOption?.values && selectedSizeInStock) {
      setSizeSelectionError(false);
      return true;
    }

    setSizeSelectionError(true);
    return false;
  };

  const onToggleCart = () => {
    if (!activeListingId || isActiveListingCartActionPending) {
      return;
    }

    if (isInCart) {
      return runOptimisticCartAction(activeListingId, false, () => {
        const result = onRemoveFromCart(activeListingId);
        if (isAuthenticated) {
          setCachedSaveCountBySeller(prevCount => Math.max(0, prevCount - 1));
        }
        return result;
      });
    }

    if (cartUnavailable) {
      return;
    }

    return runOptimisticCartAction(activeListingId, true, () =>
      addActiveListingToCart({ quantity: 1 })
    );
  };

  const onMessageClick = () => {
    if (disableActions || isOwnListing) {
      return;
    }

    onContactUser();
  };

  const onMakeOfferClick = () => {
    if (disableActions) {
      return;
    }

    if (isNegotiation && unitType === REQUEST) {
      onNavigateToMakeOfferPage();
    } else if (isNegotiation && unitType === OFFER) {
      onNavigateToRequestQuotePage();
    } else if (isPurchase) {
      onContactUser('offer');
    } else {
      onContactUser();
    }
  };

  const canonicalListingPath = createResourceLocatorString(
    'ListingPage',
    routeConfiguration,
    { id: routeListingUuid, slug: listingSlug },
    {}
  );
  const canonicalListingUrl = `${config.marketplaceRootURL}${canonicalListingPath}`;
  const shareUrl =
    typeof window !== 'undefined' && window.location?.href
      ? window.location.href
      : canonicalListingUrl;
  const shareImageUrl = facebookImages?.[0]?.url || '';

  const sellerListings = (authorListings || []).filter(l => l?.id?.uuid !== routeListingUuid);
  const hasSimilarListings = (similarListings || []).length > 0;
  const showSimilarListingsSection =
    hasSimilarFilters && hasIntersected && (fetchSimilarListingsInProgress || hasSimilarListings);
  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={metaDescription}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      {...noIndexMaybe}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'Product',
        description: description || metaDescription,
        name: title,
        image: schemaImages,
        sku: routeListingUuid,
        ...brandSchemaMaybe,
        ...(schemaColor ? { color: schemaColor } : {}),
        ...itemConditionMaybe,
        offers: {
          '@type': 'Offer',
          url: productURL,
          ...priceForSchemaMaybe(price),
          ...availabilityMaybe,
          ...itemConditionMaybe,
          ...sellerSchemaMaybe,
          hasMerchantReturnPolicy: FINAL_SALE_RETURN_POLICY,
        },
      }}
    >
      <LayoutSingleColumn className={css.pageRoot} topbar={topbar} footer={<FooterContainer />}>
        <div className={css.listingPageWrapper}>
          <div className={css.contentWrapperForProductLayout}>
            <div className={css.pdpMainLayout}>
              <div className={css.pdpGalleryColumn}>
                {mounted && currentListing.id && noPayoutDetailsSetWithOwnListing ? (
                  <ActionBarMaybe
                    className={css.actionBarForProductLayout}
                    isOwnListing={isOwnListing}
                    listing={activeListing}
                    showNoPayoutDetailsSet={noPayoutDetailsSetWithOwnListing}
                    currentUser={currentUser}
                  />
                ) : null}

                {mounted && currentListing.id && isOwnListing ? (
                  <ActionBarMaybe
                    className={css.actionBarForProductLayout}
                    isOwnListing={isOwnListing}
                    listing={activeListing}
                    currentUser={currentUser}
                    editParams={{
                      id: listingId.uuid,
                      slug: listingSlug,
                      type: listingPathParamType,
                      tab: listingTab,
                    }}
                  />
                ) : null}

                {showListingImage ? (
                  <div className={css.pdpGalleryWrapper}>
                    <SectionGallery
                      listing={activeListing}
                      variantPrefix={config.layout.listingImage.variantPrefix}
                      onManageDisableScrolling={onManageDisableScrolling}
                      soldByName={soldByName}
                      soldByLinkProps={soldByLinkProps}
                    />
                  </div>
                ) : null}

                <div className={css.pdpProtectionCard}>
                  <Icons name="security" />
                  <span className={css.pdpProtectionBody}>
                    <span className={css.pdpProtectionTitle}>
                      <FormattedMessage id="SectionProtection.title" />
                    </span>
                    <p>
                      <FormattedMessage id="SectionProtection.description" />{' '}
                      <ExternalLink href="/help/buyer-protection">
                        <FormattedMessage id="SectionProtection.learnMore" />
                      </ExternalLink>
                    </p>
                  </span>
                </div>
              </div>

              <div className={css.pdpInfoColumn}>
                <div className={css.pdpBreadcrumbs}>
                  {breadcrumbData.map((item, index) => (
                    <React.Fragment key={`${item.queryKey}-${item.queryValue}`}>
                      {index > 0 ? '/' : null}
                      <NamedLink
                        className={css.pdpBreadcrumbLink}
                        name="SearchPage"
                        to={{
                          search: `?pub_${item.queryKey}=${encodeURIComponent(item.queryValue)}`,
                        }}
                      >
                        {item.label}
                      </NamedLink>
                    </React.Fragment>
                  ))}
                </div>
                <h1 className={css.pdpTitle}>{richTitle}</h1>
                {isVacationMode ? (
                  <p className={css.vacationModeNotice} role="status">
                    <FormattedMessage id="ListingPage.sellerVacationModeUnavailable" />
                  </p>
                ) : null}
                {!isOwnListing && (isCurrentlyClosed || isOutOfStock) ? (
                  <p className={css.vacationModeNotice} role="status">
                    <FormattedMessage
                      id={
                        isCurrentlyClosed ? 'ListingPage.closedListing' : 'ListingPage.soldNotice'
                      }
                    />
                  </p>
                ) : null}
                {cachedSaveCountBySeller > 0 ? (
                  <div className={css.pdpCartMessage}>
                    <IconBagFilled />
                    <p>
                      <FormattedMessage
                        id={
                          cachedSaveCountBySeller > 1
                            ? 'ListingPage.saveCountBySellerPeople'
                            : 'ListingPage.saveCountBySeller'
                        }
                        values={{ saveCountBySeller: cachedSaveCountBySeller }}
                      />
                    </p>
                  </div>
                ) : null}
                <div className={css.pdpSocialRow}>
                  <button
                    type="button"
                    className={classNames(css.pdpIconButton, {
                      [css.pdpIconButtonActive]: isFavorited,
                    })}
                    onClick={() =>
                      !favoriteActionDisabled ? onToggleFavorite(routeListingUuid) : null
                    }
                    disabled={favoriteActionDisabled}
                    aria-pressed={isFavorited}
                    aria-label={intl.formatMessage({ id: 'ListingPage.favoriteAriaLabel' })}
                  >
                    {isFavoriteUpdating ? (
                      <IconSpinner />
                    ) : isFavorited ? (
                      <IconHeartFilled />
                    ) : (
                      <IconHeart />
                    )}
                    <span className={css.pdpLikesText}>
                      <FormattedMessage id="ListingPage.likes" values={{ count: likesCount }} />
                    </span>
                  </button>

                  {!isOwnListing && !disableActions ? (
                    <button
                      type="button"
                      className={css.pdpIconActionOnlyButton}
                      onClick={onMessageClick}
                      aria-label={intl.formatMessage({ id: 'ListingPage.messageAriaLabel' })}
                    >
                      <IconMessage />
                    </button>
                  ) : null}

                  {showCartActions ? (
                    <button
                      type="button"
                      className={classNames(css.pdpIconActionOnlyButton, {
                        [css.pdpIconButtonActive]: isInCart,
                      })}
                      onClick={onToggleCart}
                      disabled={isActiveListingCartActionPending}
                      aria-label={intl.formatMessage({
                        id: isInCart
                          ? 'ListingPage.removeFromCartAriaLabel'
                          : 'ListingPage.addToCartAriaLabel',
                      })}
                    >
                      {isInCart ? <IconBagFilled /> : <IconBag />}
                    </button>
                  ) : null}

                  <button
                    type="button"
                    className={classNames(css.pdpIconShareButton, {
                      [css.pdpIconShareButtonActive]: shareModalOpen,
                    })}
                    onClick={() => setShareModalOpen(true)}
                    aria-label={intl.formatMessage({ id: 'ListingPage.shareAriaLabel' })}
                  >
                    <Icons name="share" />
                    {/* <span className={css.pdpShareText}>
                      <FormattedMessage id="ListingPage.share" />
                    </span> */}
                  </button>
                </div>
                <p className={css.pdpDescription}>{description}</p>
                <div className={css.pdpPriceRow}>
                  {sale ? (
                    <div className={css.pdpOriginalPrice}>
                      {formatMoney(intl, sale.originalPrice)}
                    </div>
                  ) : null}
                  <div className={classNames(css.pdpPrice, { [css.pdpSalePrice]: sale })}>
                    {formattedPrice}
                  </div>
                  {sale ? (
                    <div className={css.pdpSaleBadge}>
                      <FormattedMessage
                        id="ListingPage.saleBadge"
                        values={{
                          percentOff: sale.percentOff,
                          endsAt: intl.formatDate(sale.endsAt, { month: 'short', day: 'numeric' }),
                        }}
                      />
                    </div>
                  ) : null}
                  {pdpShippingLabel ? (
                    <div className={css.pdpShippingPriceInline}>{pdpShippingLabel}</div>
                  ) : null}
                </div>
                {hasMultipleSizes ? (
                  <div className={css.pdpSizePicker}>
                    <label className={css.pdpSizeLabel} htmlFor="listing-size-selector">
                      {intl.formatMessage({
                        id: 'ListingPage.selectSize',
                        defaultMessage: 'Select size',
                      })}
                    </label>
                    <select
                      id="listing-size-selector"
                      className={css.pdpSizeSelect}
                      value={selectedSizeValue}
                      onChange={event => {
                        setSelectedSize(event.target.value);
                        setSizeSelectionError(false);
                      }}
                    >
                      <option value="" disabled>
                        {intl.formatMessage({
                          id: 'ListingPage.selectSizePlaceholder',
                          defaultMessage: 'Choose a size',
                        })}
                      </option>
                      {sizeChoicesWithListing.map(sizeChoice => (
                        <option
                          key={`${sizeChoice.values}-${sizeChoice.listingId || 'base'}`}
                          value={sizeChoice.values}
                          disabled={sizeChoice?.stock != null && sizeChoice.stock <= 0}
                        >
                          {sizeChoice.label || sizeChoice.values}
                        </option>
                      ))}
                    </select>
                    {sizeSelectionError ? (
                      <p className={css.pdpSizeError}>
                        {intl.formatMessage({
                          id: 'ListingPage.selectSizeRequired',
                          defaultMessage: 'Please select a size to continue.',
                        })}
                      </p>
                    ) : null}
                  </div>
                ) : null}
                <div className={css.pdpCtaSection}>
                  <PrimaryButton
                    type="button"
                    className={css.pdpCartButton}
                    onClick={onToggleCart}
                    disabled={disablePrimaryCartButton}
                  >
                    <FormattedMessage id={primaryCartMessageId} />
                  </PrimaryButton>
                  <SecondaryButton
                    type="button"
                    className={css.pdpOfferButton}
                    onClick={onMakeOfferClick}
                    disabled={disableActions}
                  >
                    <FormattedMessage id="ListingPage.makeAnOffer" defaultMessage="Make an offer" />
                  </SecondaryButton>
                </div>
                <div className={css.pdpMoreInformation}>
                  <h2 className={css.pdpMoreInformationTitle}>
                    <FormattedMessage id="ListingPage.moreInformation" />
                  </h2>
                  {listingDetails.length > 0 ? (
                    <ul className={css.pdpDetailsTable}>
                      {listingDetails.map(item => (
                        <li key={item.id} className={css.pdpDetailsRow}>
                          <span className={css.pdpDetailLabel}>
                            {intl.formatMessage({ id: item.id, defaultMessage: item.defaultLabel })}
                          </span>
                          <span className={css.pdpDetailValue}>{item.value}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
                <SectionTags
                  publicData={publicData}
                  history={history}
                  routeConfiguration={routeConfiguration}
                />
                <div className={css.pdpAuthorSection}>
                  <SectionAuthorMaybe
                    title={title}
                    listing={activeListing}
                    authorDisplayName={authorDisplayName}
                    onContactUser={onContactUser}
                    isInquiryModalOpen={isAuthenticated && inquiryModalOpen}
                    onCloseInquiryModal={() => {
                      setInquiryModalOpen(false);
                      setInquiryType('inquiry');
                      setPhoneVerificationRequired(false);
                      setPhoneVerificationSuccess(false);
                    }}
                    sendInquiryError={sendInquiryError}
                    sendInquiryInProgress={sendInquiryInProgress}
                    onSubmitInquiry={onSubmitInquiry}
                    currentUser={currentUser}
                    phoneVerificationRequired={phoneVerificationRequired}
                    phoneVerificationSuccess={phoneVerificationSuccess}
                    onPhoneVerified={() => {
                      setPhoneVerificationRequired(false);
                      setPhoneVerificationSuccess(true);
                    }}
                    onRefreshCurrentUser={onRefreshCurrentUser}
                    onManageDisableScrolling={onManageDisableScrolling}
                    inquiryType={inquiryType}
                  />
                </div>
              </div>
            </div>

            {hasSimilarFilters ? (
              <div className={css.similarListingsObserver} ref={similarListingsObserverRef} />
            ) : null}
            {sellerListings.length > 0 ? (
              <div className={css.pdpSellerListings}>
                <ListSlider
                  title="ListingPage.moreItemsByTheAuthor"
                  viewport={viewport}
                  listings={sellerListings}
                  intl={intl}
                  isListingPageSlider
                  isOwnListing={isOwnListing}
                  currentUser={currentUser}
                />
              </div>
            ) : null}

            {showSimilarListingsSection ? (
              <div className={css.pdpSimilarListings}>
                {fetchSimilarListingsInProgress ? (
                  <div className={css.similarListingsLoading}>
                    <IconSpinner />
                  </div>
                ) : (
                  <ListSlider
                    title="ListingPage.similarItems"
                    viewport={viewport}
                    listings={similarListings}
                    intl={intl}
                    isListingPageSlider
                    isOwnListing={isOwnListing}
                    currentUser={currentUser}
                  />
                )}
              </div>
            ) : null}
          </div>
          <ShareModal
            isOpen={shareModalOpen}
            onClose={() => setShareModalOpen(false)}
            onManageDisableScrolling={onManageDisableScrolling}
            title={title}
            url={shareUrl}
            imageUrl={shareImageUrl}
            description={description}
            source="ListingPage"
            modalId="ListingPage.share"
          />
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const showListingError = props.showListingError;
  const isVariant = props.params?.variant != null;
  const currentUser = props.currentUser;
  if (isForbiddenError(showListingError) && !isVariant && !currentUser) {
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(showListingError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (hasNoViewingRights && isForbiddenError(showListingError)) ||
    isErrorNoViewingPermission(showListingError)
  ) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      showOwnListingsOnly={hasNoViewingRights}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    timeSlotsForDate,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
    authorListingsIds,
    variantListingsIds,
    similarListingsIds,
    fetchSimilarListingsInProgress,
    saveCountBySeller,
  } = state.ListingPage;
  const { currentUser } = state.user;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };
  const viewerUserId = currentUser?.id?.uuid;
  const authorListings = filterPublicListings(
    authorListingsIds?.length ? getListingsById(state, authorListingsIds) : [],
    viewerUserId
  );
  const variantListings = filterPublicListings(
    variantListingsIds?.length ? getListingsById(state, variantListingsIds) : [],
    viewerUserId
  );
  const similarListings = similarListingsIds?.length
    ? filterPublicListings(getListingsById(state, similarListingsIds), viewerUserId)
    : [];

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    timeSlotsForDate,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    sendInquiryInProgress,
    sendInquiryError,
    favoriteIds: selectFavoriteIds(state),
    favoriteInProgressById: state.favorites?.inProgressById || {},
    cartItems: state?.cart?.items || {},
    authorListings,
    variantListings,
    similarListings,
    fetchSimilarListingsInProgress,
    saveCountBySeller,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)),
  onSendInquiry: (listing, message, offerParams) =>
    dispatch(sendInquiry(listing, message, offerParams)),
  onRefreshCurrentUser: () =>
    dispatch(
      fetchCurrentUser({
        enforce: true,
        updateHasListings: false,
        updateNotifications: false,
      })
    ),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onFetchTimeSlots: (listingId, start, end, timeZone, options) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone, options)),
  onToggleFavorite: listingId => dispatch(toggleFavorite(listingId)),
  onAddToCart: ({
    listingId,
    quantity = 1,
    sellerId,
    maxQuantity = null,
    selectedSize = null,
    selectedSizeLabel = null,
  }) =>
    dispatch(
      addItemToCart({
        listingId,
        quantity,
        maxQuantity,
        sellerId,
        selectedSize,
        selectedSizeLabel,
      })
    ),
  onRemoveFromCart: listingId => dispatch(removeItemFromCart({ listingId })),
  onFetchSimilarListings: params => dispatch(fetchSimilarListings(params)),
});

const ListingPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  withViewport
)(EnhancedListingPage);

export default ListingPage;
